import type { QueueId } from "../home/queues";
import { foldText } from "./fold";
import type { SearchTypeName } from "./search-types";

// What the typed text says besides itself (ADR-117 point 8), in the client and without a model: a prefix that
// restricts the search to a type, a code recognised by its shape that puts its types first without leaving the
// others out, and the words of a state that open the list of a home queue. Pure: the vocabulary is data.

/** The domains answer from two characters (`searchInputOf`). */
export const MIN_QUERY = 2;

/** A state is named from three characters: two are in too many words. */
const MIN_STATE_WORDS = 3;

interface SearchVocabulary<T extends string> {
  /** The word before a colon that restricts the search to one type (`ls: spring`, `ean:8001234`). */
  prefixes: Readonly<Record<string, T>>;
  /** Codes known by their shape, and the types they put first. */
  codes: readonly { shape: RegExp; types: readonly T[] }[];
}

export interface ParsedQuery<T extends string> {
  /** What the domains are asked: the text without its prefix. */
  q: string;
  /** The type a prefix restricts the search to. */
  type: T | undefined;
  /** The types a recognised code puts first. */
  first: T[];
}

export function parseQuery<T extends string>(text: string, vocabulary: SearchVocabulary<T>): ParsedQuery<T> {
  const trimmed = text.trim();
  const prefixed = /^([a-z]+)\s*:\s*(.*)$/i.exec(trimmed);
  const word = prefixed?.[1]?.toLowerCase();
  const type = word !== undefined && Object.hasOwn(vocabulary.prefixes, word) ? vocabulary.prefixes[word] : undefined;
  const q = type !== undefined ? (prefixed?.[2] ?? "").trim() : trimmed;
  const first = [...new Set(vocabulary.codes.filter((code) => code.shape.test(q)).flatMap((code) => code.types))];
  return { q, type, first };
}

/** The prefixes and codes of the types there are. */
export const vocabulary: SearchVocabulary<SearchTypeName> = {
  prefixes: {
    so: "sales_order",
    po: "purchase_order",
    ls: "linesheet",
    p: "product",
    sku: "sku",
    ean: "sku",
    org: "organization",
    ref: "reference_record",
  },
  codes: [
    // EAN-8, UPC-A, EAN-13, GTIN-14
    { shape: /^(\d{8}|\d{12,14})$/, types: ["sku"] },
    { shape: /^po[\s-]*\d+$/i, types: ["purchase_order"] },
    { shape: /^so[\s-]*\d+$/i, types: ["sales_order"] },
    { shape: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, types: ["contact", "user"] },
  ],
};

const wordsOf = (text: string) =>
  foldText(text)
    .split(/[^\p{L}\p{N}]+/u)
    .filter(Boolean);

/**
 * The queues whose state the text names: every word typed starts a word of one of the queue's phrases ("da appr"
 * names "linesheet da approvare"). In the order of the vocabulary.
 */
export function queuesNamed<Q extends string>(text: string, phrases: Readonly<Record<Q, readonly string[]>>): Q[] {
  const typed = wordsOf(text);
  if (typed.join("").length < MIN_STATE_WORDS) return [];
  return (Object.keys(phrases) as Q[]).filter((queue) =>
    phrases[queue].some((phrase) => {
      const words = wordsOf(phrase);
      return typed.every((t) => words.some((w) => w.startsWith(t)));
    }),
  );
}

/**
 * How users name the state of each home queue besides its title, which is compared as well (`useNamedQueues`): the
 * short English forms and the Italian ones.
 */
export const queuePhrases: Record<QueueId, readonly string[]> = {
  "to-approve": ["pending linesheets", "linesheet da approvare", "linesheet in attesa"],
  "to-price": ["linesheets to price", "prezzi da completare", "linesheet da prezzare"],
  "to-recalculate": ["stale prices", "prezzi da ricalcolare"],
  "integration-errors": ["failed messages", "errori di integrazione", "messaggi falliti"],
  "bc-mismatches": ["bc mismatches", "riconciliazione", "disallineamenti con bc"],
  "erp-files-to-import": ["erp files to import", "file erp da importare", "tracciati da importare"],
  "attributes-to-review": ["attributi da rivedere", "attributi da controllare"],
  "pos-to-approve": ["po to approve", "ordini di acquisto da approvare", "po da approvare"],
  "pos-to-publish": ["po to publish", "po da pubblicare"],
  "pos-to-decide": ["po changes to decide", "proposte da decidere", "modifiche po"],
  "pos-sync-failed": ["po sync failed", "po sync fallito", "po non su bc"],
  "etafs-to-review": ["etaf to review", "etaf da rivedere", "e-taf da approvare"],
  "organizations-not-on-bc": [
    "organizations sync failed",
    "clienti non su bc",
    "fornitori non su bc",
    "anagrafiche sync fallito",
  ],
  "suppliers-not-on-bc": [
    "suppliers to register",
    "fornitori da registrare",
    "fornitori senza numero bc",
    "fornitori con po aperti",
  ],
  "customers-without-order-contact": [
    "customers without buyer",
    "clienti senza contatto",
    "clienti senza buyer",
    "contatti senza ruolo",
  ],
  "bc-records-to-match": ["bc records to match", "anagrafiche bc da abbinare"],
  "in-draft": ["draft linesheets", "linesheet in bozza", "bozze"],
  "attributes-to-fill": ["attributi da compilare"],
  "pos-to-confirm": ["po to confirm", "po da confermare"],
  "pos-reminders-over": ["po not confirmed", "po non confermati", "po senza conferma dopo i reminder"],
  "inbounds-reminders-over": [
    "inbounds after the reminders",
    "inbound dopo i reminder",
    "unita mancanti senza scelta",
    "unita in piu non inviate",
  ],
  "credit-notes-reminders-over": ["credit notes not sent", "note di credito non inviate", "note di credito mancanti"],
  "invitations-reminders-over-customers": ["invitations not accepted", "inviti non accettati", "inviti clienti"],
  "invitations-reminders-over-partners": [
    "invitations not accepted",
    "inviti non accettati",
    "inviti brand",
    "inviti fornitori",
  ],
  "linesheet-invitations-reminders-over": [
    "linesheet invitations",
    "brand senza linesheet",
    "inviti alle linesheet",
    "linesheet non caricate",
  ],
  "pos-to-announce": ["documents to upload", "documenti da caricare", "po da annunciare"],
  "hubspot-records-waiting": ["hubspot records waiting", "record hubspot in attesa", "hubspot in attesa"],
  "hubspot-readings-held": ["hubspot readings held", "letture hubspot trattenute", "letture hubspot"],
  "sos-to-allocate": ["so to allocate", "orders to allocate", "ordini da allocare", "so da allocare"],
  "sos-to-lock": ["so to lock", "orders to lock", "ordini da bloccare", "so da bloccare"],
  "sos-sync-failed": ["so sync failed", "so sync fallito", "so non su bc"],
  "sos-fulfillment-stopped": ["so stopped", "processo fermo", "so ferme"],
  "sos-credit-blocked": ["credit blocked", "so credit blocked", "bloccati per credito", "ordini bloccati per credito"],
  "joor-orders-waiting": [
    "joor orders waiting",
    "ordini joor in attesa",
    "ordini di joor da risolvere",
    "joor in attesa",
  ],
  "sos-awaiting-payment": [
    "awaiting payment",
    "payments to record",
    "pagamenti da registrare",
    "in attesa di pagamento",
  ],
  "sos-deposit-due": ["deposits not paid", "depositi non pagati", "acconti non pagati"],
  "sos-uncovered": ["so without cover", "so senza copertura", "so bloccate senza copertura"],
  "sos-without-season": ["so without season", "so senza stagione", "ordini senza stagione"],
  "sos-without-confirmation": [
    "so without confirmation",
    "so senza conferma",
    "ordini senza conferma",
    "conferme da mandare",
  ],
  "sos-dunning-over": ["dunning over", "reminders over", "solleciti finiti", "solleciti esauriti"],
  "sos-confirmation-overdue": [
    "confirmations not answered",
    "order confirmations not accepted",
    "conferme non accettate",
    "conferme scadute",
  ],
  "sos-confirmation-rejected": ["confirmations rejected", "order confirmations rejected", "conferme rifiutate"],
  "short-shipments-to-credit": [
    "short shipments",
    "shipments closed short",
    "spedizioni chiuse corte",
    "note di credito da fare",
  ],
  "documents-to-retry": [
    "failed documents",
    "late documents",
    "documenti in errore",
    "documenti in ritardo",
    "fatture da richiedere",
  ],
  "documents-pdf-late": ["late pdf", "pdf in ritardo", "documenti senza pdf", "fatture senza pdf"],
  "inbounds-to-capture": ["inbounds to capture", "packing list da inserire", "inbound da acquisire"],
  "inbounds-extraction-to-review": [
    "lines read to review",
    "packing list extraction",
    "righe lette da controllare",
    "estrazione packing list",
  ],
  "inbounds-to-check": ["inbounds to check", "check 3-way", "check a tre vie", "inbound da controllare"],
  "inbounds-in-exception": ["inbounds in exception", "inbound in eccezione", "check falliti"],
  "inbounds-to-dispatch": [
    "inbounds to send to the warehouse",
    "inbound da mandare al magazzino",
    "da mandare al magazzino",
  ],
  "inbounds-to-authorize": ["inbounds to authorize", "inbound da autorizzare", "fuori tolleranza"],
  "inbounds-to-resolve": [
    "inbounds to confirm",
    "received with differences",
    "inbound ricevuti con differenze",
    "eccessi e mancanze",
  ],
  "inbounds-to-settle": ["missing units", "shortages to settle", "unità mancanti", "mancanze da scegliere"],
  "credit-notes-to-review": [
    "credit notes to review",
    "credit notes",
    "note di credito",
    "note di credito da verificare",
  ],
  "credit-notes-to-send": ["credit notes to send", "credit notes missing", "note di credito da inviare"],
  "receipts-waiting": [
    "warehouse receipts to apply",
    "receipts waiting",
    "ricezioni in attesa",
    "ricezioni del magazzino",
  ],
  "transfers-to-confirm": [
    "transfer orders to confirm",
    "intercompany transfers",
    "transfer order da confermare",
    "trasferimenti da confermare",
  ],
  "adjustments-to-confirm": [
    "stock adjustments to confirm",
    "adjustments proposed",
    "rettifiche da confermare",
    "rettifiche proposte",
  ],
  "adjustments-to-approve": [
    "stock adjustments to approve",
    "adjustments waiting for finance",
    "rettifiche da approvare",
  ],
  "vendor-returns-to-confirm": [
    "vendor returns to confirm",
    "vra waiting for the supplier",
    "resi al fornitore da confermare",
    "vra da confermare",
  ],
  "vendor-returns-refused": ["vendor returns refused", "vra refused", "resi al fornitore rifiutati", "vra rifiutate"],
  "differences-waiting": [
    "warehouse counts to apply",
    "stock counts waiting",
    "conteggi in attesa",
    "differenze di inventario in attesa",
  ],
  "movement-executions-waiting": [
    "warehouse executions to apply",
    "movement executions waiting",
    "esecuzioni in attesa",
    "movimenti del magazzino da applicare",
  ],
  "shipments-waiting": [
    "warehouse shipments to apply",
    "shipments waiting",
    "spedizioni in attesa",
    "spedizioni del magazzino da applicare",
  ],
  "fulfillments-to-book": [
    "shipments to book",
    "ddp to book",
    "tracking to record",
    "spedizioni ddp da prenotare",
    "spedizioni da prenotare",
  ],
  "fulfillments-without-ack": [
    "releases without ack",
    "releases not acknowledged",
    "rilasci senza ack",
    "rilasci non confermati dal magazzino",
  ],
  "availability-waiting": [
    "availability rows to resolve",
    "availability waiting",
    "disponibilità in attesa",
    "righe dell'ims da risolvere",
  ],
  "availability-mismatches": [
    "availability mismatches",
    "mismatches to check",
    "full publication differences",
    "differenze della fotografia",
    "differenze tra fotografia ed eventi",
  ],
  "approvals-waiting": [
    "approvals waiting",
    "waiting approvals",
    "gates waiting",
    "approvazioni in attesa",
    "cosa sta aspettando",
  ],
};
